import { wordlist } from '@scure/bip39/wordlists/english';

const BEARER = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi;
// PSBT magic identifies unkeyed console arguments too; do not require a complete
// payload because diagnostic strings may already have been truncated.
const PSBT_HEX = /70736274ff[0-9a-f]*/gi;
const PSBT_BASE64 = /cHNidP[89+/_-][A-Za-z0-9+/_=-]*/g;
// Error messages may contain serialized request headers, even when custom
// Error properties are omitted from the report.
const CREDENTIAL_NAMES = new Set([
  'xsignedmessage',
  'signedmessage',
  'userpublickey',
  'xhash',
  'xvalue',
  'xaddress',
  'xuserpublickey',
  'authorization',
  'signature',
  'sig',
  'proof',
  'password',
  'accesstoken',
  'refreshtoken',
  'privatekey',
  'privkey',
  'mnemonic',
  'seedphrase',
]);
const isCredentialName = (name: string) => CREDENTIAL_NAMES.has(name.replace(/[-_\s]/g, '').toLowerCase());

// Decode serialized diagnostic text before matching names. Bound the work, and
// fail closed when an unusually deep encoding cannot be inspected completely.
function normalizeDiagnosticText(value: string): string {
  for (let depth = 0; depth < 8; depth++) {
    const decoded = value
      .replace(/\\u([0-9a-f]{4})/gi, (_, hex: string) => String.fromCharCode(Number.parseInt(hex, 16)))
      .replace(/\\([\\"'])/g, '$1')
      .replace(/%[0-9a-f]{2}(?:%[0-9a-f]{2})*/gi, (part) => {
        try {
          return decodeURIComponent(part);
        } catch {
          return '[redacted-encoding]';
        }
      });
    if (decoded === value) return value;
    value = decoded;
  }
  return '[redacted-encoded-diagnostic]';
}

function redactCredentials(value: string, depth = 0): string {
  if (depth >= 8) return '[redacted-encoded-diagnostic]';
  try {
    const parsed: unknown = JSON.parse(value);
    const sanitize = (item: unknown, level = depth): unknown => {
      if (level >= 8) return '[redacted-nested-diagnostic]';
      if (typeof item === 'string') return redactCredentials(item, depth + 1);
      if (Array.isArray(item)) {
        if (typeof item[0] === 'string' && isCredentialName(item[0])) return [item[0], '[redacted]'];
        return item.map((entry) => sanitize(entry, level + 1));
      }
      if (item && typeof item === 'object') {
        return Object.fromEntries(
          Object.entries(item).map(([key, entry]) => [key, isCredentialName(key) ? '[redacted]' : sanitize(entry, level + 1)]),
        );
      }
      return item;
    };
    if (parsed && typeof parsed === 'object') return JSON.stringify(sanitize(parsed));
    if (typeof parsed === 'string') return JSON.stringify(redactCredentials(parsed, depth + 1));
  } catch {
    /* A stack trace or plain diagnostic does not have to be JSON. */
  }

  // Handles object properties, header entry arrays, and unquoted diagnostic
  // assignments. A credential's unquoted value can contain spaces.
  const text = normalizeDiagnosticText(value);
  const names =
    /(?<![a-z0-9_-])["']?((?:signed[ \t]+message|user[ \t]+public[ \t]+key|private[ \t]+key|seed[ \t]+phrase|access[ \t]+token|refresh[ \t]+token|[a-z][a-z0-9_-]*))["']?\s*(?::|=|,\s*(?=["']))\s*/gi;
  let result = '';
  let copied = 0;
  for (const match of text.matchAll(names)) {
    if (!isCredentialName(match[1]) || match.index < copied) continue;
    const start = match.index + match[0].length;
    // Plain diagnostics have no reliable quoting/CSV grammar. Once a secret
    // assignment starts, conservatively omit its whole line; JSON above retains
    // safe sibling fields structurally without risking an embedded-quote tail.
    const credential = text.slice(start).match(/^[^\r\n]+/)?.[0];
    if (!credential) continue;
    result += `${text.slice(copied, start)}"[redacted]"`;
    copied = start + credential.length;
  }
  return result + text.slice(copied);
}
const MNEMONIC_WORDS = new Set(wordlist);
const WIF_PRIVATE_KEY = /\b(?:[KLc][1-9A-HJ-NP-Za-km-z]{51}|[59][1-9A-HJ-NP-Za-km-z]{50})\b/gi;
const HEX_PRIVATE_KEY_CONTEXT = /\b(private\s*key|privatekey|privkey)(\s*(?:is|[:=])\s*)[a-f0-9]{64}\b/gi;

const EXTENDED_PRIVATE_KEY = /\b(?:xprv|tprv|yprv|zprv|Yprv|Zprv|uprv|vprv|Uprv|Vprv)[1-9A-HJ-NP-Za-km-z]{100,110}\b/g;
const JWT = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
// Escaped newlines/tabs (a literal backslash plus n, r or t) also separate words,
// so a JSON-stringified multi-line phrase stays in one run. A lowercase n, r or t
// after a backslash is ambiguous (`\thank` is a tab before "hank" or the path
// segment "thank"), so such a word counts as known when either reading is.
const MNEMONIC_WORD = /\b[a-z]+\b/gi;
const ESCAPE_LETTER = /^[nrt]/;
const MNEMONIC_SEPARATOR = /^(?:[\s\d\p{P}\p{S}]|\\[nrt])*$/u;

function redactMnemonicText(text: string): string {
  // One rolling 12-word window catches valid, bad-checksum and single-typo phrases.
  // Scan once; long ordinary prose stays intact unless a near-complete phrase appears.
  const output: string[] = [];
  let copied = 0;
  let start = -1;
  let end = 0;
  let count = 0;
  let mask = 0;
  let known = 0;
  let sensitive = false;
  const finish = () => {
    if (sensitive) {
      output.push(text.slice(copied, start), '[redacted-mnemonic]');
      copied = end;
    }
    start = -1;
    count = mask = known = 0;
    sensitive = false;
  };
  for (const match of text.matchAll(MNEMONIC_WORD)) {
    const word = match[0].toLowerCase();
    let index = match.index;
    let isWord = MNEMONIC_WORDS.has(word);
    if (text[index - 1] === '\\' && ESCAPE_LETTER.test(match[0])) {
      if (word.length === 1) continue;
      // Prefer the escape reading when it is a word, so a run never starts on the escape letter.
      const tailIsWord = MNEMONIC_WORDS.has(word.slice(1));
      if (tailIsWord || !isWord) {
        index++;
        isWord ||= tailIsWord;
      }
    }
    if (start >= 0 && !MNEMONIC_SEPARATOR.test(text.slice(end, index))) finish();
    if (start < 0) start = index;
    end = match.index + match[0].length;
    count++;
    if (!sensitive) {
      known -= (mask >> 11) & 1;
      const bit = isWord ? 1 : 0;
      known += bit;
      mask = ((mask << 1) | bit) & 0xfff;
      sensitive = count >= 12 && known >= 11;
    }
  }
  finish();
  output.push(text.slice(copied));
  return output.join('');
}

export function redactUrls(text: string): string {
  const clean = (value: string): string => {
    // Vercel stack positions follow the query, so preserve them before removing it.
    const position = value.match(/(?::\d+:\d+)?\)*$/)?.[0] || '';
    const address = position ? value.slice(0, -position.length) : value;
    if (address.startsWith('/')) return address.split(/[?#]/)[0] + position;
    try {
      const url = new URL(address);
      url.username = '';
      url.password = '';
      url.search = '';
      url.hash = '';
      return url.toString() + position;
    } catch {
      return `[redacted-url]${position}`;
    }
  };
  return text
    .replace(/https?:\/\/[^\s"'<>]*/gi, clean)
    .replace(/(^|[\s"'(])(\/[^\s"'<>?#]*[?#][^\s"'<>]*)/g, (_value, prefix, url) => prefix + clean(url));
}

// Redact complete credentials before applying the display limit.
export function redactString(value: string, maxLength = 20_000): string {
  return redactMnemonicText(
    redactUrls(redactCredentials(value))
      .replace(BEARER, 'Bearer [redacted]')
      .replace(PSBT_HEX, '[redacted-psbt]')
      .replace(PSBT_BASE64, '[redacted-psbt]')
      .replace(WIF_PRIVATE_KEY, '[redacted-private-key]')
      .replace(HEX_PRIVATE_KEY_CONTEXT, '$1$2[redacted-private-key]')
      .replace(EXTENDED_PRIVATE_KEY, '[redacted-private-key]')
      .replace(JWT, '[redacted-token]'),
  ).slice(0, maxLength);
}

/** Copy only sanitized error fields; provider errors can carry credential-bearing custom properties. */
export function redactError(error: Error): Error {
  const sanitized = new Error(redactString(error.message));
  sanitized.name = redactString(error.name, 300);
  sanitized.stack = error.stack ? redactString(error.stack, 100_000) : undefined;
  return sanitized;
}
